import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = await res.text();
    
    try {
      // Try to parse as JSON for structured error messages
      const errJson = JSON.parse(text);
      
      // Handle authentication errors specially
      if (res.status === 401) {
        console.error("Authentication error:", errJson);
        
        if (errJson.error === "token_expired" || errJson.message === "Invalid token" || errJson.message === "Token expired") {
          // Clear token from localStorage if it's expired or invalid
          if (localStorage.getItem('token')) {
            localStorage.removeItem('token');
            // console.log("Removed invalid token from localStorage");
          }
          throw new Error("Your session has expired. Please log in again.");
        }
      }
      
      // Use message field if available, or fallback to full error
      throw new Error(errJson.message || `${res.status}: ${text}`);
    } catch (e) {
      // If JSON parsing fails, use the original error format
      throw new Error(`${res.status}: ${text || res.statusText}`);
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  
  // Only set Content-Type to application/json if data is not FormData
  const isFormData = data instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Handle absolute and relative URLs for mobile compatibility
  let fullUrl = url;
  if (url.startsWith('/') && !url.startsWith('//')) {
    // This is a relative URL, we need to prepend the base URL
    // For mobile builds, use the environment variable
    if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
      fullUrl = `${import.meta.env.VITE_SERVER_URL}${url}`;
      console.log(`Mobile API request: Converting relative URL to absolute: ${fullUrl}`);
    }
  }

  // Log request details in debug mode
  console.log(`API Request: ${method} ${fullUrl}`, { 
    headers, 
    data: isFormData ? 'FormData object (not displayed)' : data 
  });

  try {
    const res = await fetch(fullUrl, {
      method,
      headers,
      // Don't stringify FormData, pass it directly
      body: isFormData ? data as FormData : data ? JSON.stringify(data) : undefined,
    });

    // Log response status for debugging
    console.log(`API Response Status: ${res.status} for ${method} ${fullUrl}`);
    
    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    console.error(`API Request Error: ${method} ${fullUrl}`, error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Handle URL for mobile compatibility
    let url = queryKey[0] as string;
    if (url.startsWith('/') && !url.startsWith('//')) {
      // This is a relative URL, we need to prepend the base URL
      // For mobile builds, use the environment variable
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        url = `${import.meta.env.VITE_SERVER_URL}${url}`;
        console.log(`Mobile query: Converting relative URL to absolute: ${url}`);
      }
    }

    // Log the request
    console.log(`Query Request: GET ${url}`, { headers });

    try {
      const res = await fetch(url, {
        headers
      });

      // Log response status
      console.log(`Query Response Status: ${res.status} for GET ${url}`);

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      const data = await res.json();
      return data;
    } catch (error) {
      console.error(`Query Error: GET ${url}`, error);
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});